import React from 'react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import styles from './ActivityFilter.module.css';
import {
    IonButton,
    IonIcon,
    IonLabel,
    IonListHeader,
    IonPopover,
    IonContent,
    IonList,
    IonItem,
    IonCheckbox,
    IonChip,
    IonItemDivider,
    IonSkeletonText
} from '@ionic/react';
import { closeOutline, filterOutline, star, warningOutline } from 'ionicons/icons';
import { DeviceEventCriteria } from '../models/DeviceEventCriteria';
import { useTranslation } from '../hooks/useTranslation';
import { useRfidLastSeenQuery } from '../query/rfidLastSeenQuery';
import { useRfidProfileQuery } from '../query/rfidProfileQuery';
import { getRfidDisplayLabel } from '../utils/rfidDisplay';

interface ActivityFilterProps {
    criteria: DeviceEventCriteria;
    onCriteriaChange?: (criteria: DeviceEventCriteria) => void;
}

const getSelectedRfidCodes = (criteria: DeviceEventCriteria) => {
    if (criteria.rfidCode === undefined) {
        return [];
    }

    return Array.isArray(criteria.rfidCode) ? criteria.rfidCode : [criteria.rfidCode];
};

const VisitorFilterLabel: React.FC<{ rfidCode: string; deviceId?: string; fallbackLabel?: string }> = ({ rfidCode, deviceId, fallbackLabel }) => {
    const profileQuery = useRfidProfileQuery(rfidCode, deviceId);

    if (profileQuery.isLoading && !fallbackLabel) {
        return <IonSkeletonText animated style={{ width: '88px' }} />;
    }

    return <>{getRfidDisplayLabel(rfidCode, profileQuery.data?.label ?? fallbackLabel)}</>;
};

const ActivityFilter: React.FC<ActivityFilterProps> = ({ criteria, onCriteriaChange }) => {
    const { t } = useTranslation('components');
    const rfidLastSeenQuery = useRfidLastSeenQuery();
    const [chipsContainerRef] = useAutoAnimate<HTMLDivElement>();

    const handleContrabandToggle = () => {
        const newCriteria: DeviceEventCriteria = { ...criteria };
        
        if (newCriteria.isContraband === true) {
            // Remove the filter if it's already selected
            delete newCriteria.isContraband;
        } else {
            // Set the contraband filter
            newCriteria.isContraband = true;
        }
        
        onCriteriaChange?.(newCriteria);
    };

    const handleStarredToggle = () => {
        const newCriteria: DeviceEventCriteria = { ...criteria };
        
        if (newCriteria.isStarred === true) {
            // Remove the filter if it's already selected
            delete newCriteria.isStarred;
        } else {
            // Set the starred filter
            newCriteria.isStarred = true;
        }
        
        onCriteriaChange?.(newCriteria);
    };

    const removeContrabandFilter = () => {
        const newCriteria: DeviceEventCriteria = { ...criteria };
        delete newCriteria.isContraband;
        onCriteriaChange?.(newCriteria);
    };

    const removeStarredFilter = () => {
        const newCriteria: DeviceEventCriteria = { ...criteria };
        delete newCriteria.isStarred;
        onCriteriaChange?.(newCriteria);
    };

    const handleVisitorToggle = (rfidCode: string) => {
        const newCriteria: DeviceEventCriteria = { ...criteria };
        const selectedRfidCodes = getSelectedRfidCodes(criteria);

        if (selectedRfidCodes.includes(rfidCode)) {
            const nextRfidCodes = selectedRfidCodes.filter((code) => code !== rfidCode);

            if (nextRfidCodes.length) {
                newCriteria.rfidCode = nextRfidCodes;
            } else {
                delete newCriteria.rfidCode;
            }
        } else {
            newCriteria.rfidCode = [...selectedRfidCodes, rfidCode];
        }

        onCriteriaChange?.(newCriteria);
    };

    const removeVisitorFilter = (rfidCode: string) => {
        const nextRfidCodes = getSelectedRfidCodes(criteria).filter((code) => code !== rfidCode);
        const newCriteria: DeviceEventCriteria = { ...criteria };

        if (nextRfidCodes.length) {
            newCriteria.rfidCode = nextRfidCodes;
        } else {
            delete newCriteria.rfidCode;
        }

        onCriteriaChange?.(newCriteria);
    };

    const isContrabandSelected = criteria.isContraband === true;
    const isStarredSelected = criteria.isStarred === true;
    const selectedRfidCodes = getSelectedRfidCodes(criteria);
    const visitorOptions = (rfidLastSeenQuery.data ?? [])
        .filter((visitor, index, visitors) => visitors.findIndex((option) => option.rfidCode === visitor.rfidCode) === index);

    const hasActiveFilters = isContrabandSelected || isStarredSelected || selectedRfidCodes.length > 0;

    return (
        <div className={styles.container}>
            <IonListHeader>
                <div className={styles.headerContent}>
                    {!hasActiveFilters && <IonLabel>{t('activity_filter.all_activity')}</IonLabel>}
                    <div ref={chipsContainerRef} className={styles.chipsContainer}>
                        {isStarredSelected && (
                            <IonChip
                                key="filter-starred"
                                color="dark"
                                className={styles.filterChip}
                                onClick={removeStarredFilter}
                            >
                                <IonLabel>{t('activity_filter.starred')}</IonLabel>
                                <IonIcon icon={closeOutline} />
                            </IonChip>
                        )}
                        {isContrabandSelected && (
                            <IonChip
                                key="filter-contraband"
                                color="dark"
                                className={styles.filterChip}
                                onClick={removeContrabandFilter}
                            >
                                <IonLabel>{t('activity_filter.contraband')}</IonLabel>
                                <IonIcon icon={closeOutline} />
                            </IonChip>
                        )}
                        {selectedRfidCodes.map((rfidCode) => {
                            const visitor = visitorOptions.find((visitor) => visitor.rfidCode === rfidCode);

                            return (
                                <IonChip
                                    key={`filter-visitor-${rfidCode}`}
                                    color="dark"
                                    className={styles.filterChip}
                                    onClick={() => removeVisitorFilter(rfidCode)}
                                >
                                    <IonLabel>
                                        <VisitorFilterLabel
                                            rfidCode={rfidCode}
                                            deviceId={visitor?.deviceId}
                                        />
                                    </IonLabel>
                                    <IonIcon icon={closeOutline} />
                                </IonChip>
                            );
                        })}
                    </div>
                </div>
                <IonButton
                    fill="clear"
                    id="filter-trigger"
                    aria-label={t('activity_filter.filter_options')}
                    aria-haspopup="menu"
                >
                    <IonIcon
                        slot="icon-only"
                        icon={filterOutline}
                        color="dark"
                    />
                </IonButton>
            </IonListHeader>
            
            <IonPopover
                trigger="filter-trigger"
                side="bottom"
                alignment="end"
            >
                <IonContent>
                    <IonList>
                        <IonItem
                            button
                            onClick={handleStarredToggle}
                            detail={false}
                        >
                            <IonIcon
                                icon={star}
                                slot="start"
                                color="primary"
                            />
                            <IonLabel className={styles.filterOption}>
                                {t('activity_filter.starred')}
                            </IonLabel>
                            <IonCheckbox
                                checked={isStarredSelected}
                                onIonChange={handleStarredToggle}
                                onClick={(event) => event.stopPropagation()}
                                slot="end"
                            />
                        </IonItem>
                        <IonItem
                            button
                            onClick={handleContrabandToggle}
                            detail={false}
                        >
                            <IonIcon
                                icon={warningOutline}
                                slot="start"
                                color="primary"
                            />
                            <IonLabel className={styles.filterOption}>
                                {t('activity_filter.contraband')}
                            </IonLabel>
                            <IonCheckbox
                                checked={isContrabandSelected}
                                onIonChange={handleContrabandToggle}
                                onClick={(event) => event.stopPropagation()}
                                slot="end"
                            />
                        </IonItem>
                        {(rfidLastSeenQuery.isLoading || visitorOptions.length > 0) && (
                            <IonItemDivider>
                                <IonLabel className={styles.visitorGroupLabel}>
                                    {t('activity_filter.visitors')}
                                </IonLabel>
                            </IonItemDivider>
                        )}
                        {rfidLastSeenQuery.isLoading && (
                            <IonItem lines="none">
                                <IonLabel>
                                    <IonSkeletonText animated style={{ width: '120px' }} />
                                </IonLabel>
                            </IonItem>
                        )}
                        {visitorOptions.map((visitor) => {
                            const isSelected = selectedRfidCodes.includes(visitor.rfidCode);

                            return (
                                <IonItem
                                    key={visitor.rfidCode}
                                    button
                                    onClick={() => handleVisitorToggle(visitor.rfidCode)}
                                    detail={false}
                                >
                                    <IonIcon
                                        src="/assets/rfid/tag-simple.svg"
                                        slot="start"
                                        color="primary"
                                    />
                                    <IonLabel className={styles.filterOption}>
                                        <VisitorFilterLabel
                                            rfidCode={visitor.rfidCode}
                                            deviceId={visitor.deviceId}
                                        />
                                    </IonLabel>
                                    <IonCheckbox
                                        checked={isSelected}
                                        onIonChange={() => handleVisitorToggle(visitor.rfidCode)}
                                        onClick={(event) => event.stopPropagation()}
                                        slot="end"
                                    />
                                </IonItem>
                            );
                        })}
                    </IonList>
                </IonContent>
            </IonPopover>
        </div>
    );
};

export default ActivityFilter;
